import { useState } from "react";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { AvatarUpload } from "@/components/EntityDialogs";
import { UserPlus, Edit, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const accessColor = {
  Owner: "bg-neutral-50 text-neutral-700",
  Editor: "bg-emerald-50 text-emerald-700",
  "Read-only": "bg-slate-100 text-slate-600",
};

const ROLES = ["Executive Assistant", "Personal Assistant", "Household Manager", "Chief of Staff", "Principal (viewer)", "Family member"];
const ACCESS = ["Owner", "Editor", "Read-only"];
const STATUS = ["active", "invited"];

const UserDialog = ({ open, onOpenChange, initial }) => {
  const { addWorkspaceUser, updateWorkspaceUser } = useFgp();
  const editing = !!initial;
  const blank = { name: "", email: "", role: "Personal Assistant", access: "Editor", status: "invited", avatar: "" };
  const [f, setF] = useState(initial || blank);
  // keep form in sync when opening for a different user
  const [seedId, setSeedId] = useState(initial?.id);
  if (open && initial && initial.id !== seedId) { setSeedId(initial.id); setF(initial); }
  if (open && !initial && seedId !== "new") { setSeedId("new"); setF(blank); }

  const submit = () => {
    if (!f.name.trim()) return toast.error("Name is required");
    if (!f.email.trim()) return toast.error("Email is required");
    if (editing) { updateWorkspaceUser(initial.id, f); toast.success(`${f.name} updated`); }
    else { addWorkspaceUser({ ...f, lastActive: f.status === "active" ? "Active now" : "Never" }); toast.success(`${f.name} ${f.status === "invited" ? "invited" : "added"}`); }
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit user" : "Invite user"}</DialogTitle>
          <DialogDescription>{editing ? "Update this teammate's role and access." : "Add a teammate to this workspace."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <AvatarUpload value={f.avatar} onChange={(v) => setF({ ...f, avatar: v })} fallback={f.name?.[0]} />
          <div className="space-y-1.5"><Label className="text-slate-700">Full name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" placeholder="e.g. James Whitfield" /></div>
          <div className="space-y-1.5"><Label className="text-slate-700">Email</Label>
            <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" placeholder="name@workspace.com" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-slate-700">Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-slate-700">Access</Label>
              <Select value={f.access} onValueChange={(v) => setF({ ...f, access: v })}>
                <SelectTrigger data-testid="user-access-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ACCESS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-slate-700">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="user-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="user-save-btn">{editing ? "Save changes" : "Send invite"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function WorkspaceUsers() {
  const { workspaceUsers, removeWorkspaceUser } = useFgp();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const openAdd = () => { setEditUser(null); setDialogOpen(true); };
  const openEdit = (u) => { setEditUser(u); setDialogOpen(true); };

  return (
    <div>
      <PageHeader title="User Management" subtitle="Manage who can access this workspace and what they can do."
        actions={<Button onClick={openAdd} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="invite-user"><UserPlus className="h-4 w-4" /> Invite user</Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total users", value: workspaceUsers.length },
          { label: "Active", value: workspaceUsers.filter((u) => u.status === "active").length },
          { label: "Invited", value: workspaceUsers.filter((u) => u.status === "invited").length },
          { label: "Read-only", value: workspaceUsers.filter((u) => u.access === "Read-only").length },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{s.label}</p>
            <p className="mt-2 font-heading text-2xl font-semibold text-slate-900">{s.value}</p>
          </div>
        ))}
      </div>

      {workspaceUsers.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <UserPlus className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-700">No team members yet</p>
          <p className="text-xs text-slate-400 mt-1 mb-4">Invite assistants, household managers or principals to collaborate.</p>
          <Button onClick={openAdd} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="empty-invite-user"><UserPlus className="h-4 w-4" /> Invite your first user</Button>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>User</TableHead>
                <TableHead className="hidden sm:table-cell">Role</TableHead>
                <TableHead>Access</TableHead>
                <TableHead className="hidden md:table-cell">Status</TableHead>
                <TableHead className="hidden lg:table-cell">Last active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workspaceUsers.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9"><AvatarImage src={u.avatar} /><AvatarFallback className="bg-neutral-50 text-neutral-600 text-sm">{u.name[0]}</AvatarFallback></Avatar>
                      <div><p className="text-sm font-medium text-slate-800">{u.name}</p><p className="text-xs text-slate-400">{u.email}</p></div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-slate-500 text-sm">{u.role}</TableCell>
                  <TableCell><span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${accessColor[u.access]}`}>{u.access}</span></TableCell>
                  <TableCell className="hidden md:table-cell"><StatusBadge status={u.status} /></TableCell>
                  <TableCell className="hidden lg:table-cell text-slate-400 text-sm">{u.lastActive}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(u)} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-neutral-600 hover:bg-slate-50" data-testid={`edit-user-${u.id}`}><Edit className="h-4 w-4" /></button>
                      {u.access !== "Owner" && (
                        <button onClick={() => setToDelete(u)} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </motion.div>
      )}

      <UserDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editUser} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>They will immediately lose access to this workspace. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-user"
              onClick={() => { removeWorkspaceUser(toDelete.id); toast.success(`${toDelete.name} removed`); setToDelete(null); }}>Remove user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
